import {KS_DOMAIN, BR_SHOULD_NOT_ENCODED} from '../constants'
import {onClient} from '@salesforce/commerce-sdk-react/utils'

// Priority levels for scheduling
// Higher priority (runs sooner): After Paint > Next Task > Idle
// Lower priority (runs later): Idle is for analytics that can wait

export const scheduleNextTask = (callback) => {
    if (!onClient()) {
        return undefined
    }

    return window.setTimeout(callback, 0)
}

export const yieldToMain = async () => {
    return new Promise((resolve) => {
        scheduleNextTask(resolve)
    })
}

/**
 * Schedule analytics after a short delay and then wait for browser idle time.
 * This keeps third-party tag work out of the click/presentation window for INP.
 *
 * @param {Function} callback - Analytics function to execute
 * @param {number} minDelay - Minimum delay in ms before execution (default: 100)
 * @param {number} idleTimeout - Maximum idle wait in ms before execution (default: 2000)
 * @returns {{cancel: Function}|undefined} Cancellable schedule handle or undefined
 */
export const scheduleIdleAnalytics = (callback, minDelay = 100, idleTimeout = 2000) => {
    if (!onClient()) {
        return undefined
    }

    let delayId
    let idleId
    let fallbackId
    let cancelled = false
    let completed = false

    const clearScheduledWork = () => {
        if (delayId !== undefined) {
            window.clearTimeout(delayId)
            delayId = undefined
        }

        if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(idleId)
            idleId = undefined
        }

        if (fallbackId !== undefined) {
            window.clearTimeout(fallbackId)
            fallbackId = undefined
        }
    }

    const run = (deadline) => {
        if (cancelled || completed) {
            return
        }

        completed = true
        clearScheduledWork()
        window.removeEventListener('pagehide', flushOnPageHide)
        callback(deadline)
    }

    // A hard navigation can happen before the idle callback. Flush on pagehide so
    // moving tracking off the interaction task does not silently drop the event.
    const flushOnPageHide = () => run()

    const cancel = () => {
        cancelled = true
        clearScheduledWork()
        window.removeEventListener('pagehide', flushOnPageHide)
    }

    window.addEventListener('pagehide', flushOnPageHide, {once: true})

    delayId = window.setTimeout(() => {
        delayId = undefined

        if (cancelled) {
            return
        }

        if (typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(
                (deadline) => {
                    idleId = undefined
                    run(deadline)
                },
                {timeout: idleTimeout}
            )
            return
        }

        fallbackId = window.setTimeout(() => {
            fallbackId = undefined
            run()
        }, 0)
    }, Math.max(Number(minDelay) || 0, 0))

    return {cancel}
}

/**
 * Schedule a callback to run after the browser paints (using requestAnimationFrame).
 * Useful for deferring work that shouldn't block paint, but should run soon.
 *
 * @param {Function} callback - Function to execute after paint
 * @returns {number|undefined} Animation frame ID or undefined
 */
export const scheduleAfterPaint = (callback) => {
    if (!onClient()) {
        return undefined
    }

    if (typeof window.requestAnimationFrame !== 'undefined') {
        return window.requestAnimationFrame(() => {
            // Wait for two frames to ensure paint is complete
            return window.requestAnimationFrame(callback)
        })
    }

    return scheduleNextTask(callback)
}

export const setBrCookie = () => {
    const timestamp = Date.now()
    const brCookie = encodeURIComponent(
        `uid=${Math.round(Math.random() * 10e12)}:v=17.1:ts=${timestamp}:hc=1`
    )
    if (onClient()) {
        document.cookie = `_br_uid_2=${brCookie};domain=${KS_DOMAIN};path=/`
    }
    return brCookie
}

export const getOrCreateBrUid2 = (cookieStorage) => {
    if (!onClient()) {
        return undefined
    }

    const existingBrUid = cookieStorage?.get('_br_uid_2')
    if (existingBrUid) {
        return existingBrUid
    }

    return setBrCookie()
}

function getValue(key, value) {
    if (Array.isArray(value)) {
        return value.map((v) => getValue(key, v)).join('&')
    }
    return `${key}=${BR_SHOULD_NOT_ENCODED.includes(key) ? value : encodeURIComponent(value)}`
}

export const buildApiUrl = (base, params) => {
    const url = new URL(base)

    url.search = Object.entries(params)
        ?.filter(([, value]) => value !== undefined)
        ?.map(([key, value]) => {
            return getValue(key, value)
        })
        ?.join('&')
    return url
}

const loadShippingSwitcherHandler = () => {
    try {
        // auto preselect geo country
        if (
            !window.GlobalE.GetCookie('GlobalE_Geo_Popup') &&
            window.geScriptLoaderData.geoLocationCountry.countryCode &&
            window.geScriptLoaderData.geoLocationCountry.isCountryExists &&
            window.geScriptLoaderData.geoLocationCountry.countryCode !==
                window.geScriptLoaderData.country
        ) {
            window.GlobalE.SetCookie(
                'GlobalE_Welcome_Data',
                {showWelcome: false},
                window.GlobalE.GE_DATA_COOKIE_EXP,
                true
            )
        }

        // set 'GlobalE_Geo_Popup' cookie
        if (!window.GlobalE.GetCookie('GlobalE_Geo_Popup')) {
            window.GlobalE.SetCookie('GlobalE_Geo_Popup', true, window.GlobalE.GE_DATA_COOKIE_EXP)
        }
    } catch (e) {
        console.error(e)
    }
}

export const shippingSwitcherConfigs = (isYR, country, culture, currency) => {
    if (!window.GlobalE) {
        return
    }
    // 'window.GlobalE.LoadWelcome' event
    if (window.geScriptLoaderData.globaleOperatedCountry) {
        /** NOTE: Note a reactJS practice, but required to set data-brand on <html> for brand-specific UI. **/
        document
            .querySelector('html')
            .setAttribute('data-brand', isYR ? 'yellowrose' : 'kendrascott')
        window.GlobalE.LoadWelcome(country, culture, currency)
    }

    // 'window.GlobalE.LoadShippingSwitcher' event
    window.GlobalE.LoadShippingSwitcher(
        country,
        culture,
        currency,
        false,
        loadShippingSwitcherHandler
    )
}

export const getStoreAddress = (store) => {
    return {
        address1: store.address1 || '',
        city: store.city,
        countryCode: 'US', // Hardcode
        postalCode: store.postalCode || store.zip || '',
        stateCode: store.stateCode,
        firstName: store.name,
        lastName: '.',
        fullName: store.name,
        phone: store.phone || ''
    }
}

export const hashEmail = async (email) => {
    const encoder = new TextEncoder()
    const data = encoder.encode(email)
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    return hashHex
}

export const getTextAlignment = (position) => {
    if (position === 'top-right' || position === 'middle-right' || position === 'bottom-right') {
        return 'right'
    }
    if (position === 'top-left' || position === 'middle-left' || position === 'bottom-left') {
        return 'left'
    }
    if (
        position === 'top-center' ||
        position === 'middle-center' ||
        position === 'bottom-center' ||
        position === 'left-half' ||
        position === 'right-half'
    ) {
        return 'center'
    }
    return 'center'
}
